import axios, { AxiosResponse } from "axios";
import React, { useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { LoadingRedirect } from "src/components/LoadingRedirect/LoadingRedirect";
import {
  getSelectedProvider,
  storeLocalStorageData
} from "../../actions/SelectProvider";
import { setProviderCallbackUrl } from "../../actions/SetProviderCallbackUrl";
import Config from "../../config";
import { evaluateProviderMigration, useUserFamilyType } from "../../hooks/be";
import { ErrorResponse } from "../../hooks/errorReponse";
import { useQueryParams } from "../../hooks/queryParams";
import { Provider } from "../../types";
import { UserFamilyType } from "../Family/familyType";
import { GetOIDCToken } from "src/actions/OIDC";

type Params = {
  providerId: string;
  serviceId: string;
};

const slashAllowed: string[] = [
  "643132d0-bd1a-4860-846a-360e9bf8b8e3",
  "cf4b215d-b67d-4d08-9abd-579bf7eab8c4",
  "cc97c30c-e5a5-41cc-969b-727b0d6cd707",
  "af822e17-828c-4c4c-a324-1e0ad2bf1020"
];

const formatCallbackUrl = (url: string, providerId: string): string => {
  if (!slashAllowed.includes(providerId)) {
    if (url.endsWith("/")) {
      return url.slice(0, -1);
    }
  }
  return url;
};

const TIMEOUT_REDIRECT = 3000;

const ProviderIntegrator = () => {
  const [providerInfo, setProviderInfo] = useState<Provider>();
  const { providerId, serviceId } = useParams<Params>() as Params;
  const queryParams = useQueryParams();
  const callbackUrl: string = queryParams.get("callback") || "";
  const providerCallbackUrl: string = formatCallbackUrl(
    queryParams.get("provider_callback_url") || "",
    providerId
  );

  const provider = getSelectedProvider();

  const flow: string = queryParams.get("flow") || "gsat";
  localStorage.setItem("flowOrigin", flow);

  const urlCallback =
    flow === "alf" ? queryParams.get("redirect_uri") : providerCallbackUrl;

  const { userType } = useUserFamilyType();
  const [errorAPI, setErrorAPI] = useState<boolean>(false);
  const [errorCode, setErrorCode] = useState<number>();
  const [errorId, setErrorId] = useState<string>("");
  const [userTypeResponse, setuserTypeResponse] = useState<string>("");
  const [redirectUrl, setRedirectURL] = useState<string>("");

  useEffect(() => {
    userType()
      .then(data => {
        setErrorId(data.errorId);
        setuserTypeResponse(data.type);
      })
      .catch((error: ErrorResponse) => {
        setErrorCode(error.code);
        setErrorId(error.errorId);
        setErrorAPI(true);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (
      userTypeResponse !== "" &&
      userTypeResponse !== UserFamilyType.Granted &&
      !errorAPI
    ) {
      const fetchProviderState = async () => {
        let url = Config.INT_BASE_URL;
        if (await evaluateProviderMigration(providerId)) {
          url = Config.BE_BASE_URL;
        }

        try {
          const response: AxiosResponse<Provider> = await axios.get(
            `${url}/v1/providers/${providerId}/state`,
            {
              headers: {
                Authorization: `Bearer ${GetOIDCToken()}`
              }
            }
          );
          setProviderInfo(response.data);

          setProviderCallbackUrl(
            response.data.client_id_for_alf,
            urlCallback || ""
          );
        } catch (error) {
          const errorResponse = error as ErrorResponse;
          setErrorCode(errorResponse.code);
          setErrorId(errorResponse.errorId);
          setErrorAPI(true);
        }
      };

      fetchProviderState();
    }
  }, [userTypeResponse, errorAPI, providerId, urlCallback]);

  useEffect(() => {
    if (!errorAPI && providerInfo !== undefined) {
      storeLocalStorageData(providerInfo, serviceId, callbackUrl);
      if (providerInfo.is_default_authentication) {
        const providerCallbackUrl = localStorage.getItem("providerCallbackUrl");
        const url =
          providerInfo.url.indexOf("redirect_uri") > -1
            ? `${providerInfo.url}`
            : `${providerInfo.url}&redirect_uri=${providerCallbackUrl}`;
        setRedirectURL(url);
      } else {
        setRedirectURL(
          `/signin?logo=${providerInfo.logo}&name=${providerInfo.name}`
        );
      }
    }
  }, [
    providerInfo,
    errorAPI,
    providerId,
    callbackUrl,
    providerCallbackUrl,
    serviceId
  ]);

  if (
    (errorAPI && userTypeResponse === "") ||
    (errorAPI && providerInfo === undefined)
  ) {
    return (
      <Navigate to={`/error?error_code=${errorCode}&error_id=${errorId}`} />
    );
  } else if (userTypeResponse === UserFamilyType.Granted) {
    return <Navigate to={`/family?error_id=${errorId}`} />;
  } else if (
    providerInfo !== undefined &&
    providerInfo?.url !== "" &&
    redirectUrl !== ""
  ) {
    setTimeout(() => {
      window.location.href = redirectUrl;
    }, TIMEOUT_REDIRECT);
  }

  return (
    <LoadingRedirect
      logo={provider?.logo_white ?? ""}
      name={provider?.name ?? ""}
    />
  );
};

export default ProviderIntegrator;
